import React, { createContext, useContext, useEffect, useState } from "react";

// Define the user type
interface User {
  id: number;
  name: string;
  email: string;
  is_admin: boolean;
  stripe_account_id?: string;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  setToken: (token: string | null) => void;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  logout: () => void;
  redirectPath: string;
  setRedirectPath: (path: string) => void;
  setRedirectUrl: (url: string) => void;
  selectedLocation: string | null;
  setLocation: (location: string | null) => void;
  selectedLocationZip: string | null;
  setLocationZip: (location: string | null) => void;
  showAdminPanel: boolean;
  setIsAdminPanelState: (isAdminPanel: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setTokenState] = useState<string | null>(() => {
    return localStorage.getItem("token");
  });

  const [selectedLocation, setSelectedLocation] = useState<string | null>(
    () => {
      return localStorage.getItem("location");
    }
  );

  const setLocation = (newLocation: string | null) => {
    if (newLocation) {
      localStorage.setItem("location", newLocation);
    } else {
      localStorage.removeItem("location");
    }
    setSelectedLocation(newLocation);
  };

  const [selectedLocationZip, setSelectedLocationZip] = useState<string | null>(
    () => {
      return localStorage.getItem("location_zip");
    }
  );

  const setLocationZip = (newLocation: string | null) => {
    if (newLocation) {
      localStorage.setItem("location_zip", newLocation);
    } else {
      localStorage.removeItem("location_zip");
    }
    setSelectedLocationZip(newLocation);
  };

  const [user, setUserState] = useState<User | null>(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [redirectPath, setRedirectPath] = useState<string>(() => {
    const stored = localStorage.getItem("redirectPath");
    return stored ? stored : "/";
  });

  const [showAdminPanel, setShowAdminPanel] = useState<boolean>(() => {
    const stored = localStorage.getItem("isAdminPanel");
    return stored ? JSON.parse(stored) : false;
  });

  const setIsAdminPanelState = (isAdminPanel: boolean) => {
    if (isAdminPanel) {
      localStorage.setItem("isAdminPanel", isAdminPanel.toString());
    } else {
      localStorage.removeItem("isAdminPanel");
    }
    setShowAdminPanel(isAdminPanel);
  };

  const setToken = (newToken: string | null) => {
    if (newToken) {
      localStorage.setItem("token", newToken);
    } else {
      localStorage.removeItem("token");
    }
    setTokenState(newToken);
  };

  const setUser = (newUser: User | null) => {
    if (newUser) {
      localStorage.setItem("user", JSON.stringify(newUser));
    } else {
      localStorage.removeItem("user");
    }
    setUserState(newUser);
  };

  const setRedirectUrl = (url: string) => {
    localStorage.setItem("redirectPath", url);
    setRedirectPath(url);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAdminPanelState(false);
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        setToken,
        setUser,
        isAuthenticated,
        logout,
        redirectPath,
        setRedirectPath,
        setRedirectUrl,
        selectedLocation,
        setLocation,
        selectedLocationZip,
        setLocationZip,
        showAdminPanel,
        setIsAdminPanelState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
